import { buildPortalLogoutUrl } from '@evolve/common-ui';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { useEffect, useState } from 'react';
import { deleteLocalStorageItem, getLocalStorageItem } from '../utils';

// * Mapped EvolveRH User Profiles type to id
export enum PROFILES {
  ADMINISTRADOR_DE_SISTEMA = 1,
  ADMINISTRACAO = 2,
  DIRECAO_COMERCIAL = 3,
  RESPONSAVEL_DE_AGENCIA = 4,
  CONSULTOR_RECURSOS_HUMANOS = 5,
  TECNICO_DE_RECRUTAMENTO = 6,
  GESTOR_DE_CLIENTE = 7,
  DIRECAO_ADMINISTRATIVA_E_FINANCEIRA = 8,
  GESTOR_DE_COBRANCA = 9,
  ADMINISTRATIVO = 10,
  ESTAGIARIO = 11,
  COORDENADOR = 12,
  DESIGNER_GRAFICO = 13,
  CONSULTOR_COMERCIAL = 14,
  DIRETOR_ZONA = 15,
}

export enum COMPANIES {
  EVOLVE_ETT = 5,
  EVOLVE_GRH = 6,
  ANADOLU = 2,
}

// * Admin profiles that should be able to interact with something features that ordinary users cannot.
// * Eg.: Some filters may show an additional input to select agencies, which are only interactable by admin users.
export const PROFILES_ADMIN = [
  PROFILES.ADMINISTRADOR_DE_SISTEMA,
  PROFILES.ADMINISTRACAO,
  PROFILES.DIRECAO_COMERCIAL,
];

type TUserAgency = {
  id: string;
  name: string;
  acronym: string;
  active: number;
  address: string;
  location: string;
  postalCode: string;
};

// * EvolveRH authenticates users structure. Feel free to add more as needed.
export type TUserJWTPayload = JwtPayload & {
  // * User ID
  id: number;
  // * User profile ID (which are mapped on PROFILES enum)
  profileId: number;
  // * User agency ID
  agencyId: number;
  // * User agency (object)
  agency?: TUserAgency;
  // * User agencies (array of objects)
  agencies?: TUserAgency[];
  // * User name
  name: string;
  // * User email
  email: string;
  // * User acronym
  acronym: string;
  // * User claims (shiro perms)
  claims: string[];
};

export const useAuthenticatedUser = () => {
  const [user, setUser] = useState<TUserJWTPayload>();
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  // * Flag to check if the logged in user is an admin
  const isAdmin = !!user && PROFILES_ADMIN.includes(user.profileId);

  // * Flag to check if the logged in user has multiple agencies associated to it
  const hasMultipleAgencies = !!user?.agencies && user.agencies.length > 1;

  // * Logs out the authenticated user by deleting the stored access_token and setting the authenticated user to undefined.
  const logout = () => {
    deleteLocalStorageItem('access_token');
    setUser(undefined);

    // * Redirects user to logout page

    // const isBrowser = () => typeof window !== "undefined";
    // if(isBrowser()){
    //   window.location.href = `${process.env.GATSBY_LEGACY_PORTAL_URL}/users/logout`;
    // }

    window.location.href = buildPortalLogoutUrl();
  };

  // * Checks if there are an user JWT (access_token) set in the local storage.
  // * If so, decodes it and set the authenticated user
  useEffect(() => {
    const token = getLocalStorageItem('access_token');

    if (token) {
      const authenticatedUser = jwtDecode<TUserJWTPayload>(token);
      setUser(authenticatedUser);
    }

    setIsLoadingUser(false);
  }, []);

  return { user, isAdmin, logout, isLoadingUser, hasMultipleAgencies };
};
